/**
 * Get filepond library translation
 *
 * @param locale
 * @returns {*}
 */
export function getFilePondTrans(locale) {
    const lang = {
        en: extend(filePondEN(), filePondDefault()),
        lt: extend(filePondLT(), filePondDefault())
    };

    return lang[locale] ?? {};
}

/**
 * Function to extend objects (similar to jQuery's $.extend)
 *
 * @param target
 * @param source
 * @returns {*}
 */
function extend(target, source) {
    for (const key in source) {
        if (source.hasOwnProperty(key)) {
            if (typeof source[key] === 'object' && source[key] !== null && !Array.isArray(source[key])) {
                target[key] = extend(target[key] || {}, source[key]);
            } else {
                target[key] = source[key];
            }
        }
    }
    return target;
}

/**
 * Filepond default language parameters
 */
function filePondDefault() {
    return {
        fileValidateTypeLabelExpectedTypesMap: {
            'text/plain': '.txt',
            'application/pdf': '.pdf',
            'application/msword': '.doc',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
            'application/vnd.ms-excel': '.xls',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
            'application/x-zip-compressed': 'zip',
            'application/zip': null,
            'application/vnd.oasis.opendocument.text': '.odt',
            'image/jpeg': '.jpg, .jpeg',
            'image/png': '.png',
            'image/gif': '.gif'
        }
    };
}

/**
 * Filepond LT translation
 */
function filePondLT() {
    return {
        labelIdle: 'Nutempkite failus čia arba Pasirinkite',
        labelInvalidField: 'Įkelti netinkami failai',
        labelFileWaitingForSize: 'Nurodykite dydi',
        labelFileSizeNotAvailable: 'Dydis nepalaikomas',
        labelFileLoading: 'Laukiama',
        labelFileLoadError: 'Klaida',
        labelFileProcessing: 'Įkeliama',
        labelFileProcessingComplete: 'Įkėlimas baigtas',
        labelFileProcessingAborted: 'Įkėlimas atšauktas',
        labelFileProcessingError: 'Įkėlimo klaida',
        labelFileProcessingRevertError: 'Klaida grąžinant',
        labelFileRemoveError: 'Klaida pašalinant',
        labelTapToCancel: 'paspauskite, jei norite atšaukti',
        labelTapToRetry: 'paspauskite ir bandykite dar kartą',
        labelTapToUndo: 'paspauskite, kad anuliuoti veiksmą',
        labelButtonRemoveItem: 'Pašalinti',
        labelButtonAbortItemLoad: 'Nutraukta',
        labelButtonRetryItemLoad: 'Bandykite dar kartą',
        labelButtonAbortItemProcessing: 'Atšaukti',
        labelButtonUndoItemProcessing: 'Atšaukti paskutinį veiksmą',
        labelButtonRetryItemProcessing: 'Bandykite dar kartą',
        labelButtonProcessItem: 'Krovimas',
        labelMaxFileSizeExceeded: 'Failas viršija maksimalų leidžiamą dydį',
        labelMaxFileSize: 'Maksimalus failo dydis: {filesize}',
        labelMaxTotalFileSizeExceeded: 'Viršytas maksimalus bendras dydis',
        labelMaxTotalFileSize: 'Maksimalus bendras failų dydis: {filesize}',
        labelFileTypeNotAllowed: 'Netinkamas failo tipas',
        fileValidateTypeLabelExpectedTypes: 'Laukiama {allButLastType} arba {lastType}',
        imageValidateSizeLabelFormatError: 'Paveikslėlio tipas nepalaikomas',
        imageValidateSizeLabelImageSizeTooSmall: 'Paveikslėlio dydis yra per mažas',
        imageValidateSizeLabelImageSizeTooBig: 'Paveikslėlis viršija maksimalų leidžiamą dydį',
        imageValidateSizeLabelExpectedMinSize: 'Mažiausias dydis: {minWidth} × {minHeight}',
        imageValidateSizeLabelExpectedMaxSize: 'Maksimalus dydis: {maxWidth} × {maxHeight}',
        imageValidateSizeLabelImageResolutionTooLow: 'Rezoliucija per maža',
        imageValidateSizeLabelImageResolutionTooHigh: 'Rezoliucija per didelė',
        imageValidateSizeLabelExpectedMinResolution: 'Mažiausia rezoliucija: {minResolution}',
        imageValidateSizeLabelExpectedMaxResolution: 'Didžiausia rezoliucija: {maxResolution}'
    };
}

/**
 * Filepond EN translation
 */
function filePondEN() {
    return {
        labelIdle: 'Drag & Drop your files or Browse ',
        labelInvalidField: 'Field contains invalid files',
        labelFileWaitingForSize: 'Waiting for size',
        labelFileSizeNotAvailable: 'Size not available',
        labelFileLoading: 'Loading',
        labelFileLoadError: 'Error during load',
        labelFileProcessing: 'Uploading',
        labelFileProcessingComplete: 'Upload complete',
        labelFileProcessingAborted: 'Upload cancelled',
        labelFileProcessingError: 'Error during upload',
        labelFileProcessingRevertError: 'Error during revert',
        labelFileRemoveError: 'Error during remove',
        labelTapToCancel: 'tap to cancel',
        labelTapToRetry: 'tap to retry',
        labelTapToUndo: 'tap to undo',
        labelButtonRemoveItem: 'Remove',
        labelButtonAbortItemLoad: 'Abort',
        labelButtonRetryItemLoad: 'Retry',
        labelButtonAbortItemProcessing: 'Cancel',
        labelButtonUndoItemProcessing: 'Undo',
        labelButtonRetryItemProcessing: 'Retry',
        labelButtonProcessItem: 'Upload',
        labelMaxFileSizeExceeded: 'File is too large',
        labelMaxFileSize: 'Maximum file size is {filesize}',
        labelMaxTotalFileSizeExceeded: 'Maximum total size exceeded',
        labelMaxTotalFileSize: 'Maximum total file size is {filesize}',
        labelFileTypeNotAllowed: 'File of invalid type',
        fileValidateTypeLabelExpectedTypes: 'Expects {allButLastType} or {lastType}',
        imageValidateSizeLabelFormatError: 'Image type not supported',
        imageValidateSizeLabelImageSizeTooSmall: 'Image is too small',
        imageValidateSizeLabelImageSizeTooBig: 'Image is too big',
        imageValidateSizeLabelExpectedMinSize: 'Minimum size is {minWidth} × {minHeight}',
        imageValidateSizeLabelExpectedMaxSize: 'Maximum size is {maxWidth} × {maxHeight}',
        imageValidateSizeLabelImageResolutionTooLow: 'Resolution is too low',
        imageValidateSizeLabelImageResolutionTooHigh: 'Resolution is too high',
        imageValidateSizeLabelExpectedMinResolution: 'Minimum resolution is {minResolution}',
        imageValidateSizeLabelExpectedMaxResolution: 'Maximum resolution is {maxResolution}',
    };
}
